import { v4 as uuidv4 } from 'uuid';
import { scrubPII, scrubPIIFromObject } from './utils/piiScrubber.js';

// Specialized Grading Strategies
class MultipleChoiceGrader {
  constructor(agent) {
    this.agent = agent;
  }

  async grade(correctAnswer, studentAnswer, maxScore, rubric) {
    const isCorrect = this.normalizeAnswer(studentAnswer) === this.normalizeAnswer(correctAnswer);
    
    return {
      studentAnswer,
      correctAnswer,
      similarityScore: isCorrect ? 'High' : 'Low',
      value: isCorrect ? maxScore : 0,
      maxScore,
      feedback: isCorrect ? 
        'Correct answer!' : 
        `Incorrect. The correct answer is ${correctAnswer}.`,
      rubricBreakdown: {
        accuracy: { score: isCorrect ? maxScore : 0, feedback: isCorrect ? 'Perfect' : 'Incorrect choice' }
      }
    };
  }

  normalizeAnswer(answer) {
    return answer.toString().trim().toUpperCase().replace(/[^A-D0-9]/g, '');
  }
}

class EssayGrader {
  constructor(agent) {
    this.agent = agent;
  }

  async grade(correctAnswer, studentAnswer, maxScore, rubric) {
    const structureAnalysis = this.analyzeEssayStructure(studentAnswer);
    
    const prompt = `
    Grade this essay answer with detailed analysis:
    
    Expected Answer: ${correctAnswer}
    Student Essay: ${studentAnswer}
    Max Score: ${maxScore}
    
    Structure Analysis: ${JSON.stringify(structureAnalysis)}
    
    Provide detailed feedback on:
    1. Thesis clarity and strength
    2. Argument development and evidence
    3. Organization and flow
    4. Conclusion effectiveness
    5. Writing mechanics
    
    ${this.agent.createAdvancedGradingPrompt(correctAnswer, studentAnswer, rubric, maxScore)}
    `;
    
    const response = await this.agent.callLLM(prompt);
    const result = this.agent.parseLLMResponse(response, correctAnswer, studentAnswer, maxScore);
    result.structureAnalysis = structureAnalysis;
    
    return result;
  }

  analyzeEssayStructure(essay) {
    const paragraphs = essay.split('\n\n').filter(p => p.trim());
    const sentences = essay.split(/[.!?]+/).filter(s => s.trim());
    const wordCount = essay.split(/\s+/).length;
    
    return {
      paragraphCount: paragraphs.length,
      sentenceCount: sentences.length,
      wordCount,
      avgSentenceLength: Math.round(wordCount / sentences.length),
      hasIntroduction: this.detectIntroduction(paragraphs),
      hasConclusion: this.detectConclusion(paragraphs[paragraphs.length - 1])
    };
  }

  detectIntroduction(paragraph) {
    const introIndicators = /\b(introduction|first|initially|begin|start|topic|subject)\b/i;
    return introIndicators.test(paragraph);
  }

  detectConclusion(paragraph) {
    const conclusionIndicators = /\b(conclusion|finally|in summary|to conclude|overall|in the end)\b/i;
    return conclusionIndicators.test(paragraph);
  }
}

class ShortAnswerGrader {
  constructor(agent) {
    this.agent = agent;
  }

  async grade(correctAnswer, studentAnswer, maxScore, rubric) {
    const prompt = this.agent.createAdvancedGradingPrompt(correctAnswer, studentAnswer, rubric, maxScore);
    const response = await this.agent.callLLM(prompt);
    return this.agent.parseLLMResponse(response, correctAnswer, studentAnswer, maxScore);
  }
}

class MathematicalGrader {
  constructor(agent) {
    this.agent = agent;
  }

  async grade(correctAnswer, studentAnswer, maxScore, rubric) {
    // Extract numerical values and check mathematical equivalence
    const correctNums = this.extractNumbers(correctAnswer);
    const studentNums = this.extractNumbers(studentAnswer);
    
    const numericalMatch = this.compareNumerical(correctNums, studentNums);
    
    const prompt = `
    Grade this mathematical answer:
    
    Expected Answer: ${correctAnswer}
    Student Answer: ${studentAnswer}
    Numerical Match: ${numericalMatch ? 'Yes' : 'No'}
    
    Consider:
    1. Correct final answer
    2. Work shown/methodology
    3. Units and formatting
    4. Mathematical reasoning
    
    ${this.agent.createAdvancedGradingPrompt(correctAnswer, studentAnswer, rubric, maxScore)}
    `;
    
    const response = await this.agent.callLLM(prompt);
    const result = this.agent.parseLLMResponse(response, correctAnswer, studentAnswer, maxScore);
    result.numericalMatch = numericalMatch;
    
    return result;
  }

  extractNumbers(text) {
    const matches = text.match(/-?\d+\.?\d*/g);
    return matches ? matches.map(num => parseFloat(num)) : [];
  }

  compareNumerical(correct, student, tolerance = 0.01) {
    if (correct.length !== student.length) return false;
    
    return correct.every((num, index) => 
      Math.abs(num - student[index]) <= tolerance
    );
  }
}

class CodeGrader {
  constructor(agent) {
    this.agent = agent;
  }

  async grade(correctAnswer, studentAnswer, maxScore, rubric) {
    const codeAnalysis = this.analyzeCode(studentAnswer);
    
    const prompt = `
    Grade this code answer:
    
    Expected Solution: ${correctAnswer}
    Student Code: ${studentAnswer}
    
    Code Analysis: ${JSON.stringify(codeAnalysis)}
    
    Evaluate:
    1. Correctness of logic
    2. Code efficiency
    3. Code style and readability
    4. Error handling
    5. Best practices
    
    ${this.agent.createAdvancedGradingPrompt(correctAnswer, studentAnswer, rubric, maxScore)}
    `;
    
    const response = await this.agent.callLLM(prompt);
    const result = this.agent.parseLLMResponse(response, correctAnswer, studentAnswer, maxScore);
    result.codeAnalysis = codeAnalysis;
    
    return result;
  }

  analyzeCode(code) {
    return {
      lineCount: code.split('\n').length,
      hasComments: /\/\/|\/\*|\#/.test(code),
      hasFunctions: /function|def |class /.test(code),
      hasLoops: /for |while |forEach/.test(code),
      hasConditionals: /if |switch|case/.test(code)
    };
  }
}

class TrueFalseGrader {
  constructor(agent) {
    this.agent = agent;
  }

  async grade(correctAnswer, studentAnswer, maxScore, rubric) {
    const correct = this.normalizeBoolean(correctAnswer);
    const student = this.normalizeBoolean(studentAnswer);
    const isCorrect = correct === student;
    
    return {
      studentAnswer,
      correctAnswer,
      similarityScore: isCorrect ? 'High' : 'Low',
      value: isCorrect ? maxScore : 0,
      maxScore,
      feedback: isCorrect ? 
        'Correct!' : 
        `Incorrect. The correct answer is ${correctAnswer}.`,
      rubricBreakdown: {
        accuracy: { score: isCorrect ? maxScore : 0, feedback: isCorrect ? 'Correct' : 'Incorrect' }
      }
    };
  }

  normalizeBoolean(answer) {
    const normalized = answer.toString().toLowerCase().trim();
    if (['true', 't', '1', 'yes', 'correct'].includes(normalized)) return true;
    if (['false', 'f', '0', 'no', 'incorrect'].includes(normalized)) return false;
    return null;
  }
}

// Main GradingAgent Class
class GradingAgent {
  constructor(answerKey, studentAnswers, similarityThreshold = 60, selectedLLM = 'deepseek', options = {}) {
    this.answerKey = answerKey;
    this.studentAnswers = studentAnswers;
    this.similarityThreshold = similarityThreshold;
    this.selectedLLM = selectedLLM;
    this.options = {
      onProgress: options.onProgress || (() => {}),
      enableCache: options.enableCache !== false,
      timeout: options.timeout || 30000,
      useConsensus: options.useConsensus || false,
      batchSize: options.batchSize || 5,
      maxRetries: options.maxRetries || 3,
      ...options
    };
    
    // Store PII replacements for restoring in results
    this.piiReplacements = new Map();

    // Store current full description for freeform question context
    this.currentFullDescription = '';

    // API Keys - now passed from context instead of environment variables
    // Supports: deepseek, openai, gemini (strings), ollama (object), custom (object)
    const providedKeys = options.apiKeys || {};
    this.apiKeys = {
      deepseek: providedKeys.deepseek || null,
      openai: providedKeys.openai || null,
      gemini: providedKeys.gemini || null
    };

    // Ollama configuration (local/remote)
    this.ollamaConfig = providedKeys.ollama || null;

    // Custom OpenAI-compatible endpoint
    this.customConfig = providedKeys.custom || null;

    // Validate API keys
    this.validateApiKeys();
    
    // Cache for results
    this.cache = new Map();
    this.cacheDuration = 60 * 60 * 1000; // 1 hour
    
    // Initialize grading strategies
    this.gradingStrategies = {
      multiple_choice: new MultipleChoiceGrader(this),
      essay: new EssayGrader(this),
      short_answer: new ShortAnswerGrader(this),
      mathematical: new MathematicalGrader(this),
      code: new CodeGrader(this),
      true_false: new TrueFalseGrader(this)
    };
  }
  
  validateApiKeys() {
    const availableProviders = [];

    if (this.apiKeys.deepseek) availableProviders.push('DeepSeek');
    if (this.apiKeys.openai) availableProviders.push('OpenAI');
    if (this.apiKeys.gemini) availableProviders.push('Gemini');
    if (this.ollamaConfig) availableProviders.push('Ollama');
    if (this.customConfig?.apiKey && this.customConfig?.endpoint) availableProviders.push('Custom');

    if (availableProviders.length === 0) {
      console.warn('No API providers configured. Please set up at least one provider.');
    } else {
      console.log(`Available providers: ${availableProviders.join(', ')}`);
    }

    // Auto-select best available provider if current selection is not available
    if (!this.isProviderAvailable(this.selectedLLM)) {
      const fallback = this.getFirstAvailableProvider();
      if (fallback) {
        console.log(`Selected provider ${this.selectedLLM} not available, using ${fallback}`);
        this.selectedLLM = fallback;
      }
    }
  }

  isProviderAvailable(provider) {
    switch (provider) {
      case 'deepseek': return !!this.apiKeys.deepseek;
      case 'openai': return !!this.apiKeys.openai;
      case 'gemini': return !!this.apiKeys.gemini;
      case 'ollama': return !!this.ollamaConfig;
      case 'custom': return !!(this.customConfig?.apiKey && this.customConfig?.endpoint);
      default: return false;
    }
  }

  getFirstAvailableProvider() {
    const order = ['ollama', 'deepseek', 'openai', 'gemini', 'custom'];
    for (const provider of order) {
      if (this.isProviderAvailable(provider)) return provider;
    }
    return null;
  }

  // Main comparison method with consensus option
  async compareAnswers() {
    if (this.options.useConsensus) {
      return this.compareAnswersWithConsensus();
    }
    
    return this.compareAnswersBatch(this.options.batchSize);
  }

  async compareAnswersWithConsensus() {
    const models = ['deepseek', 'openai', 'gemini', 'ollama', 'custom'].filter(model => this.isProviderAvailable(model));
    
    if (models.length < 2) {
      console.warn('Consensus grading requires at least 2 models. Falling back to single model.');
      return this.compareAnswersBatch();
    }

    try {
      this.options.onProgress({ status: 'starting', message: 'Initializing consensus grading...' });
      
      const parsedAnswerKey = this.parseAnswerKey();
      const parsedStudentAnswers = this.parseStudentAnswers();
      const results = [];

      for (let i = 0; i < parsedStudentAnswers.length; i++) {
        const studentAnswer = parsedStudentAnswers[i];
        const matchingQuestion = parsedAnswerKey.find(q => 
          q.questionNumber === studentAnswer.questionNumber
        );

        if (!matchingQuestion) {
          results.push(this.createNoMatchResult(studentAnswer));
          continue;
        }

        this.options.onProgress({ 
          status: 'processing', 
          message: `Consensus grading question ${studentAnswer.questionNumber} (${i + 1}/${parsedStudentAnswers.length})...`,
          progress: (i / parsedStudentAnswers.length) * 100
        });

        // Grade with multiple models
        const modelResults = await Promise.allSettled(
          models.map(async (model) => {
            const tempAgent = new GradingAgent(this.answerKey, this.studentAnswers, this.similarityThreshold, model, {
              ...this.options,
              useConsensus: false
            });
            return tempAgent.gradeSingleAnswer(
              matchingQuestion.correctAnswer,
              studentAnswer.answer,
              matchingQuestion.maxScore,
              matchingQuestion.rubric,
              matchingQuestion.questionType,
              matchingQuestion.fullDescription || ''
            );
          })
        );

        const successfulResults = modelResults
          .filter(result => result.status === 'fulfilled')
          .map(result => result.value);

        if (successfulResults.length === 0) {
          results.push(this.createErrorResult(studentAnswer, matchingQuestion, 'All models failed'));
          continue;
        }

        // Calculate consensus
        const consensusResult = this.calculateConsensus(
          successfulResults, 
          studentAnswer.questionNumber, 
          matchingQuestion.maxScore
        );
        results.push(consensusResult);
      }

      this.options.onProgress({ status: 'complete', message: 'Consensus grading complete!' });
      return results;
      
    } catch (error) {
      console.error('Error in consensus grading:', error);
      this.options.onProgress({ status: 'error', message: `Consensus grading failed: ${error.message}` });
      throw error;
    }
  }

  calculateConsensus(modelResults, questionNumber, maxScore) {
    const scores = modelResults.map(r => r.value);
    const feedbacks = modelResults.map(r => r.feedback);
    
    // Statistical consensus
    const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    const variance = scores.reduce((sum, score) => sum + Math.pow(score - avgScore, 2), 0) / scores.length;
    const confidence = Math.max(0, 1 - (variance / (maxScore * maxScore)));

    return {
      questionNumber,
      studentAnswer: modelResults[0].studentAnswer,
      correctAnswer: modelResults[0].correctAnswer,
      value: Math.round(avgScore),
      maxScore,
      similarityScore: this.calculateConsensusSimilarity(modelResults.map(r => r.similarityScore)),
      confidence: Math.round(confidence * 100),
      modelScores: scores,
      consensusFeedback: this.mergeFeedback(feedbacks),
      individualFeedback: feedbacks,
      variance: Math.round(variance * 100) / 100,
      additionalContext: modelResults[0].additionalContext || '',
    }
  }

  calculateConsensusSimilarity(similarities) {
    const scoreMap = { 'High': 3, 'Medium': 2, 'Low': 1 };
    const avgScore = similarities.reduce((sum, sim) => sum + (scoreMap[sim] || 2), 0) / similarities.length;
    
    if (avgScore >= 2.5) return 'High';
    if (avgScore >= 1.5) return 'Medium';
    return 'Low';
  }

  mergeFeedback(feedbacks) {
    // Extract common themes and merge feedback
    const commonPhrases = this.findCommonPhrases(feedbacks);
    const uniquePoints = this.extractUniquePoints(feedbacks);
    
    return `${commonPhrases.join(' ')} ${uniquePoints.join(' ')}`.trim();
  }

  findCommonPhrases(feedbacks) {
    // Simple implementation - in production, use more sophisticated NLP
    const words = feedbacks.join(' ').toLowerCase().split(/\s+/);
    const wordCount = {};
    
    words.forEach(word => {
      if (word.length > 3) {
        wordCount[word] = (wordCount[word] || 0) + 1;
      }
    });
    
    return Object.entries(wordCount)
      .filter(([word, count]) => count > 1)
      .map(([word]) => word)
      .slice(0, 5);
  }

  extractUniquePoints(feedbacks) {
    // Extract unique sentences from each feedback
    const sentences = feedbacks.flatMap(feedback => 
      feedback.split(/[.!?]+/).map(s => s.trim()).filter(s => s.length > 10)
    );
    
    return [...new Set(sentences)].slice(0, 3);
  }

  async compareAnswersBatch(batchSize = 5) {
    try {
      this.options.onProgress({ status: 'starting', message: 'Initializing batch grading...' });
      
      const parsedAnswerKey = this.parseAnswerKey();
      const parsedStudentAnswers = this.parseStudentAnswers();
      
      const results = [];
      const batches = this.createBatches(parsedStudentAnswers, batchSize);
      
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        this.options.onProgress({ 
          status: 'processing', 
          message: `Processing batch ${i + 1}/${batches.length}...`,
          progress: (i / batches.length) * 100
        });
        
        const batchPromises = batch.map(async (studentAnswer) => {
          const matchingQuestion = parsedAnswerKey.find(q => 
            q.questionNumber === studentAnswer.questionNumber
          );
          
          if (!matchingQuestion) {
            return this.createNoMatchResult(studentAnswer);
          }
          
          return this.gradeSingleAnswerWithCache(
            matchingQuestion.correctAnswer,
            studentAnswer.answer,
            matchingQuestion.maxScore || 10,
            matchingQuestion.rubric || { accuracy: 50, completeness: 30, clarity: 20 },
            studentAnswer.questionNumber,
            matchingQuestion.questionType || 'short_answer',
            matchingQuestion.fullDescription || ''
          );
        });
        
        const batchResults = await Promise.allSettled(batchPromises);
        
        batchResults.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value) {
            results.push(result.value);
          } else {
            console.error(`Failed to grade question ${batch[index].questionNumber}:`, result.reason);
            results.push(this.createErrorResult(batch[index], null, result.reason?.message || 'Unknown error'));
          }
        });
        
        // Rate limiting between batches
        if (i < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      this.options.onProgress({ status: 'complete', message: 'Batch grading complete!' });
      return results;
      
    } catch (error) {
      console.error('Error in batch grading:', error);
      this.options.onProgress({ status: 'error', message: `Batch grading failed: ${error.message}` });
      throw error;
    }
  }

  createBatches(array, batchSize) {
    const batches = [];
    for (let i = 0; i < array.length; i += batchSize) {
      batches.push(array.slice(i, i + batchSize));
    }
    return batches;
  }

  async gradeSingleAnswerWithCache(correctAnswer, studentAnswer, maxScore, rubric, questionNumber, questionType = 'short_answer', fullDescription = '') {
    const cacheKey = this.generateCacheKey(correctAnswer, studentAnswer, questionType);

    if (this.options.enableCache) {
      const cachedResult = this.getFromCache(cacheKey);
      if (cachedResult) {
        this.options.onProgress({
          status: 'cached',
          message: `Using cached result for question ${questionNumber}`
        });
        return { ...cachedResult, questionNumber };
      }
    }

    const result = await this.gradeSingleAnswer(correctAnswer, studentAnswer, maxScore, rubric, questionType, fullDescription);
    result.questionNumber = questionNumber;
    
    // Apply similarity threshold for additional context
    if (this.shouldFetchAdditionalContext(result.similarityScore)) {
      this.options.onProgress({ 
        status: 'enhancing', 
        message: `Fetching additional context for question ${questionNumber}...` 
      });
      result.additionalContext = await this.fetchAdditionalContext(correctAnswer);
    }
    
    if (this.options.enableCache) {
      this.saveToCache(cacheKey, result);
    }
    
    return result;
  }
  
  parseAnswerKey() {
    try {
      if (typeof this.answerKey === 'string') {
        const parsed = JSON.parse(this.answerKey);
        return parsed.questions || [];
      }
      return this.answerKey.questions || [];
    } catch (error) {
      throw new Error(`Failed to parse answer key: ${error.message}`);
    }
  }
  
  parseStudentAnswers() {
    try {
      if (typeof this.studentAnswers === 'string') {
        const parsed = JSON.parse(this.studentAnswers);
        return parsed.answers || [];
      }
      return this.studentAnswers.answers || [];
    } catch (error) {
      throw new Error(`Failed to parse student answers: ${error.message}`);
    }
  }
  
  generateCacheKey(correctAnswer, studentAnswer, questionType = '') {
    const key = `${this.selectedLLM}_${questionType}_${correctAnswer}_${studentAnswer}`;
    return key.replace(/\s+/g, '_').substring(0, 200); // Limit key length
  }
  
  getFromCache(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheDuration) {
      return cached.result;
    }
    return null;
  }
  
  saveToCache(key, result) {
    this.cache.set(key, {
      result,
      timestamp: Date.now()
    });
  }
  
  shouldFetchAdditionalContext(similarityScore) {
    if (similarityScore === 'Low') return true;
    if (similarityScore === 'Medium' && this.similarityThreshold > 70) return true;
    return false;
  }

  getGradingStrategy(questionType) {
    return this.gradingStrategies[questionType] || this.gradingStrategies.short_answer;
  }
  
  async gradeSingleAnswer(correctAnswer, studentAnswer, maxScore, rubric, questionType = 'short_answer', fullDescription = '') {
    const gradingStrategy = this.getGradingStrategy(questionType);

    // Store fullDescription for use in grading prompt
    this.currentFullDescription = fullDescription;

    try {
      // Scrub PII from student answer before sending to LLM
      const piiResult = scrubPII(studentAnswer, {
        scrubNames: true,
        scrubEmails: true,
        scrubPhones: true,
        scrubSSNs: true,
        scrubCreditCards: true,
        scrubIPs: true,
        scrubDates: false, // Dates might be relevant for assignments
        scrubURLs: false, // URLs might be relevant for references
        preserveContext: true
      });

      // Store replacements for this answer
      piiResult.replacements.forEach((original, placeholder) => {
        this.piiReplacements.set(placeholder, original);
      });

      const scrubbedStudentAnswer = piiResult.scrubbedText;

      // Log that we're using LLM grading
      console.log(`[GradingAgent] Grading question type: ${questionType}, using LLM: ${this.selectedLLM}`);
      if (fullDescription) {
        console.log(`[GradingAgent] Using full description context for grading`);
      }
      if (piiResult.replacements.size > 0) {
        console.log(`[GradingAgent] PII scrubbed: ${piiResult.replacements.size} items removed`);
      }
      this.options.onProgress({
        status: 'processing',
        message: `Using ${this.selectedLLM} to grade answer...`
      });

      // Use scrubbed answer for LLM grading
      const result = await gradingStrategy.grade(correctAnswer, scrubbedStudentAnswer, maxScore, rubric);
      
      // Restore original student answer in result (for display)
      if (result.studentAnswer) {
        result.studentAnswer = studentAnswer; // Keep original for display
      }
      
      // Check if result came from LLM or fallback
      if (result.feedback && result.feedback.includes('Automatic grading failed')) {
        console.warn('[GradingAgent] LLM grading failed, used fallback text comparison');
        this.options.onProgress({ 
          status: 'warning', 
          message: 'LLM grading unavailable, using text comparison' 
        });
      } else {
        console.log('[GradingAgent] LLM grading successful');
      }
      
      // Enhanced processing
      const processedRubric = this.processRubric(rubric, maxScore);
      result.rubricBreakdown = result.rubricBreakdown || await this.generateRubricBreakdown(
        correctAnswer, studentAnswer, processedRubric, result.value
      );
      
      // Add plagiarism and AI detection
      result.integrityCheck = await this.detectPlagiarismAndAI(studentAnswer, correctAnswer);
      
      return result;
      
    } catch (error) {
      console.error('[GradingAgent] Error grading answer:', error);
      this.options.onProgress({ 
        status: 'error', 
        message: `Grading error: ${error.message}` 
      });
      return this.createErrorResult({ answer: studentAnswer }, { correctAnswer, maxScore }, error.message);
    }
  }

  processRubric(rubric, maxScore) {
    const totalWeight = Object.values(rubric).reduce((sum, weight) => sum + weight, 0);
    const normalizedRubric = {};
    
    Object.entries(rubric).forEach(([criterion, weight]) => {
      normalizedRubric[criterion] = {
        weight: (weight / totalWeight) * 100,
        maxPoints: Math.round((weight / totalWeight) * maxScore),
        description: this.getCriterionDescription(criterion)
      };
    });
    
    return normalizedRubric;
  }

  getCriterionDescription(criterion) {
    const descriptions = {
      accuracy: "Factual correctness and precision of information",
      completeness: "Coverage of all required elements and thoroughness",
      clarity: "Clear expression and organization of ideas",
      originality: "Creative thinking and unique insights",
      grammar: "Proper grammar, spelling, and mechanics",
      analysis: "Depth of analysis and critical thinking",
      examples: "Use of relevant examples and evidence"
    };
    return descriptions[criterion.toLowerCase()] || "General assessment criterion";
  }

  async generateRubricBreakdown(correctAnswer, studentAnswer, rubric, totalScore) {
    const breakdown = {};
    
    for (const [criterion, details] of Object.entries(rubric)) {
      const criterionScore = Math.round((totalScore / Object.keys(rubric).length));
      breakdown[criterion] = {
        score: Math.min(criterionScore, details.maxPoints),
        maxPoints: details.maxPoints,
        feedback: await this.generateCriterionFeedback(criterion, correctAnswer, studentAnswer, criterionScore, details.maxPoints)
      };
    }
    
    return breakdown;
  }

  async generateCriterionFeedback(criterion, correctAnswer, studentAnswer, score, maxPoints) {
    const percentage = (score / maxPoints) * 100;
    
    const feedbackTemplates = {
      accuracy: {
        high: "Factually accurate and precise",
        medium: "Mostly accurate with minor errors",
        low: "Contains significant factual errors"
      },
      completeness: {
        high: "Comprehensive and thorough coverage",
        medium: "Good coverage but missing some elements",
        low: "Incomplete, missing key components"
      },
      clarity: {
        high: "Very clear and well-organized",
        medium: "Generally clear with some confusion",
        low: "Unclear and poorly organized"
      }
    };
    
    const level = percentage >= 80 ? 'high' : percentage >= 60 ? 'medium' : 'low';
    return feedbackTemplates[criterion]?.[level] || `${criterion}: ${level} performance`;
  }
  
  createAdvancedGradingPrompt(correctAnswer, studentAnswer, rubric, maxScore) {
    const rubricText = Object.entries(rubric)
      .map(([criterion, weight]) => `${criterion}: ${weight}%`)
      .join(', ');

    // Include full description context if available (for freeform questions)
    const fullDescriptionContext = this.currentFullDescription
      ? `\nFULL GRADING CONTEXT: "${this.currentFullDescription}"\n(Use this context to understand the complete grading criteria and key points expected in the answer)\n`
      : '';

    return `You are an expert grading assistant. Compare the student's answer with the correct answer and provide detailed evaluation.
${fullDescriptionContext}
CORRECT ANSWER: "${correctAnswer}"
STUDENT ANSWER: "${studentAnswer}"
MAX SCORE: ${maxScore}
RUBRIC: ${rubricText}

Provide a comprehensive evaluation including:
1. Similarity score (High/Medium/Low)
2. Numerical score (0-${maxScore})
3. Detailed feedback explaining strengths and weaknesses
4. Specific suggestions for improvement
5. Recognition of any creative or insightful elements

Consider partial credit for:
- Correct concepts even if incompletely expressed
- Good reasoning even with minor errors
- Creative approaches that show understanding
${this.currentFullDescription ? '\nIMPORTANT: Use the FULL GRADING CONTEXT above to evaluate whether the student addressed all key points and requirements specified by the instructor.\n' : ''}
Respond in JSON format:
{
  "similarityScore": "High/Medium/Low",
  "value": number,
  "feedback": "detailed feedback string",
  "suggestions": "improvement suggestions string",
  "strengths": "what the student did well",
  "weaknesses": "areas needing improvement"
}`;
  }
  
  async callLLM(prompt) {
    return this.callLLMWithRetry(prompt, this.options.maxRetries);
  }

  async callLLMWithRetry(prompt, maxRetries = 3) {
    let lastError;
    const originalModel = this.selectedLLM;
    
    // Check if API key is available
    if (!this.isProviderAvailable(this.selectedLLM)) {
      const errorMsg = `No API key configured for ${this.selectedLLM}. Please configure your API keys in Settings.`;
      console.error(`[GradingAgent] ${errorMsg}`);
      this.options.onProgress({ 
        status: 'error', 
        message: errorMsg 
      });
      throw new Error(errorMsg);
    }
    
    console.log(`[GradingAgent] Calling LLM: ${this.selectedLLM} with prompt length: ${prompt.length}`);
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        this.options.onProgress({ 
          status: 'processing', 
          message: `Calling ${this.selectedLLM} API (attempt ${attempt}/${maxRetries})...` 
        });
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.options.timeout);
        
        let result;
        try {
          console.log(`[GradingAgent] Making API call to ${this.selectedLLM}...`);
          switch (this.selectedLLM) {
            case 'deepseek':
              result = await this.callDeepSeekAPI(prompt, controller.signal);
              break;
            case 'openai':
              result = await this.callOpenAIAPI(prompt, controller.signal);
              break;
            case 'gemini':
              if (!this.apiKeys.gemini || this.apiKeys.gemini.trim() === '') {
                throw new Error('Gemini API key is not configured. Please add your API key in Settings.');
              }
              result = await this.callGeminiAPI(prompt, controller.signal);
              break;
            case 'ollama':
              result = await this.callOllamaAPI(prompt, controller.signal);
              break;
            case 'custom':
              result = await this.callCustomAPI(prompt, controller.signal);
              break;
            default:
              throw new Error(`Unsupported LLM: ${this.selectedLLM}`);
          }
          console.log(`[GradingAgent] API call successful, response length: ${result?.length || 0}`);
        } finally {
          clearTimeout(timeoutId);
        }
        
        // Validate response quality
        if (this.validateLLMResponse(result)) {
          console.log(`[GradingAgent] LLM response validated successfully`);
          return result;
        } else {
          console.warn(`[GradingAgent] LLM response validation failed, response:`, result?.substring(0, 100));
          throw new Error('Invalid or low-quality response from LLM');
        }
        
      } catch (error) {
        lastError = error;
        console.error(`[GradingAgent] Attempt ${attempt} failed:`, error.message);
        console.error(`[GradingAgent] Error details:`, error);
        
        this.options.onProgress({ 
          status: 'warning', 
          message: `${this.selectedLLM} attempt ${attempt} failed: ${error.message}` 
        });
        
        if (attempt < maxRetries) {
          // Exponential backoff
          const delay = Math.pow(2, attempt) * 1000;
          console.log(`[GradingAgent] Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          
          // Try different model if available on second attempt
          if (attempt === 2) {
            const fallbackModel = this.getFallbackModel();
            if (fallbackModel && fallbackModel !== this.selectedLLM) {
              console.log(`[GradingAgent] Switching to fallback model: ${fallbackModel}`);
              this.selectedLLM = fallbackModel;
            }
          }
        }
      }
    }
    
    // Restore original model
    this.selectedLLM = originalModel;
    const finalError = `All ${maxRetries} attempts failed. Last error: ${lastError.message}`;
    console.error(`[GradingAgent] ${finalError}`);
    this.options.onProgress({ 
      status: 'error', 
      message: finalError 
    });
    throw new Error(finalError);
  }

  getFallbackModel() {
    const allProviders = ['deepseek', 'openai', 'gemini', 'ollama', 'custom'];
    const availableModels = allProviders.filter(
      model => this.isProviderAvailable(model) && model !== this.selectedLLM
    );

    return availableModels.length > 0 ? availableModels : null;
  }

  validateLLMResponse(response) {
    if (!response || response.trim().length < 10) return false;
    
    // Check for JSON structure if expected
    if (response.includes('{') && response.includes('}')) {
      try {
        const jsonMatch = response.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          JSON.parse(jsonMatch);
          return true;
        }
      } catch (e) {
        return false;
      }
    }
    
    return response.length > 20; // Basic length check
  }
  
  async callDeepSeekAPI(prompt, signal) {
    if (!this.apiKeys.deepseek) {
      throw new Error('DeepSeek API key not configured');
    }
    
    console.log('[GradingAgent] Calling DeepSeek API...');
    
    const response = await fetch('https://api.deepseek.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKeys.deepseek}`
      },
      body: JSON.stringify({
        model: 'deepseek-chat',
        messages: [
          {
            role: 'system',
            content: 'You are a helpful grading assistant. Always respond with valid JSON.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.3,
        max_tokens: 1000
      }),
      signal
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[GradingAgent] DeepSeek API error:', response.status, errorText);
      throw new Error(`DeepSeek API error: ${response.status} ${response.statusText}. ${errorText.substring(0, 200)}`);
    }
    
    const data = await response.json();
    const content = data.choices?.[0]?.message?.content || '';
    console.log('[GradingAgent] DeepSeek API response received, length:', content.length);
    return content;
  }
  
  async callOpenAIAPI(prompt, signal) {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKeys.openai}`
      },
      body: JSON.stringify({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: 'You are a helpful grading assistant. Always respond with valid JSON.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.3,
        max_tokens: 1000
      }),
      signal
    });
    
    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.choices?.[0]?.message?.content || '';
  }
  
  async callGeminiAPI(prompt, signal) {
    // Validate API key first
    if (!this.apiKeys.gemini || this.apiKeys.gemini.trim() === '') {
      throw new Error('Gemini API key is missing or empty. Please configure your API key in Settings.');
    }

    // Use gemini-2.0-flash as default
    const model = 'gemini-2.0-flash';
    
    // Properly encode the API key in the URL
    const apiKey = encodeURIComponent(this.apiKeys.gemini.trim());
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`;
    
    console.log(`[GradingAgent] Calling Gemini API with model: ${model}`);
    console.log(`[GradingAgent] API key present: ${this.apiKeys.gemini ? 'Yes' : 'No'}, length: ${this.apiKeys.gemini?.length || 0}`);
    
    const requestBody = {
      contents: [
        {
          parts: [
            {
              text: prompt
            }
          ]
        }
      ],
      generationConfig: {
        temperature: 0.3,
        maxOutputTokens: 2000
      }
    };

    console.log(`[GradingAgent] Request body size: ${JSON.stringify(requestBody).length} bytes, prompt length: ${prompt.length}`);

    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody),
        signal
      });
    } catch (fetchError) {
      console.error('[GradingAgent] Fetch error:', fetchError);
      if (fetchError.name === 'AbortError') {
        throw new Error('Gemini API request timed out. Please try again.');
      }
      if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('NetworkError')) {
        throw new Error('Network error connecting to Gemini API. Please check your internet connection.');
      }
      throw new Error(`Failed to connect to Gemini API: ${fetchError.message}`);
    }

    // Check for HTTP errors
    if (!response.ok) {
      let errorMessage = `Gemini API error: ${response.status} ${response.statusText}`;
      let errorDetails = null;
      
      try {
        const errorData = await response.json();
        errorDetails = errorData;
        console.error('[GradingAgent] Gemini API error response:', errorData);
        
        if (errorData.error?.message) {
          errorMessage = `Gemini API error (${response.status}): ${errorData.error.message}`;
        } else if (errorData.error) {
          errorMessage = `Gemini API error (${response.status}): ${JSON.stringify(errorData.error)}`;
        }
      } catch (e) {
        // Try to get error text if JSON parsing fails
        try {
          const errorText = await response.text();
          console.error('[GradingAgent] Gemini API error text:', errorText);
          if (errorText) {
            errorMessage = `Gemini API error (${response.status}): ${errorText.substring(0, 200)}`;
          }
        } catch (textError) {
          console.error('[GradingAgent] Could not parse error response');
        }
      }
      
      // Provide helpful error messages for common status codes
      if (response.status === 400) {
        errorMessage += ' (Bad Request - check your API key format and request body)';
      } else if (response.status === 401) {
        errorMessage += ' (Unauthorized - invalid API key)';
      } else if (response.status === 403) {
        errorMessage += ' (Forbidden - API key may not have Gemini API enabled)';
      } else if (response.status === 429) {
        errorMessage += ' (Rate limit exceeded - too many requests)';
      } else if (response.status === 500) {
        errorMessage += ' (Internal server error - try again later)';
      }
      
      throw new Error(errorMessage);
    }

    // Parse response
    let data;
    try {
      data = await response.json();
      console.log('[GradingAgent] Gemini API response received, candidates:', data.candidates?.length || 0);
    } catch (parseError) {
      console.error('[GradingAgent] Failed to parse Gemini API response:', parseError);
      throw new Error('Invalid JSON response from Gemini API. The service may be experiencing issues.');
    }
    
    // Check for errors in response body (Gemini can return errors with 200 status)
    if (data.error) {
      console.error('[GradingAgent] Gemini API returned error in response:', data.error);
      throw new Error(`Gemini API error: ${data.error.message || JSON.stringify(data.error)}`);
    }
    
    // Check if candidates array exists and has content
    if (!data.candidates || data.candidates.length === 0) {
      console.error('[GradingAgent] Gemini API returned no candidates. Response:', data);
      throw new Error('Gemini API returned no candidates. The response may have been blocked or filtered. Check your API key permissions.');
    }
    
    // Check for finish reason (blocked, safety, etc.)
    const candidate = data.candidates[0];
    if (candidate.finishReason && candidate.finishReason !== 'STOP') {
      const reason = candidate.finishReason;
      console.warn(`[GradingAgent] Gemini API finish reason: ${reason}`);
      throw new Error(`Gemini API response was ${reason}. This may indicate content safety filtering or other issues.`);
    }
    
    // Extract text content
    const text = candidate.content?.parts?.[0]?.text;
    if (!text || text.trim().length === 0) {
      console.error('[GradingAgent] Gemini API returned empty text. Candidate:', candidate);
      throw new Error('Gemini API returned empty response. The model may have been blocked or returned no content.');
    }
    
    console.log(`[GradingAgent] Gemini API success, response length: ${text.length}`);
    return text;
  }

  async callOllamaAPI(prompt, signal) {
    if (!this.ollamaConfig) {
      throw new Error('Ollama not configured');
    }

    const endpoint = this.ollamaConfig.endpoint || 'http://localhost:11434';
    const model = this.ollamaConfig.model || 'llama2';

    const response = await fetch(`${endpoint}/api/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: model,
        prompt: `You are a helpful grading assistant. Always respond with valid JSON.\n\n${prompt}`,
        stream: false,
        options: {
          temperature: 0.3
        }
      }),
      signal
    });

    if (!response.ok) {
      throw new Error(`Ollama API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.response || '';
  }

  async callCustomAPI(prompt, signal) {
    if (!this.customConfig?.endpoint || !this.customConfig?.apiKey) {
      throw new Error('Custom API not configured');
    }

    const response = await fetch(this.customConfig.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.customConfig.apiKey}`
      },
      body: JSON.stringify({
        model: this.customConfig.model || 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: 'You are a helpful grading assistant. Always respond with valid JSON.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.3,
        max_tokens: 1000
      }),
      signal
    });

    if (!response.ok) {
      throw new Error(`Custom API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.choices?.[0]?.message?.content || '';
  }
  
  parseLLMResponse(response, correctAnswer, studentAnswer, maxScore) {
    try {
      // Log the raw response for debugging
      console.log('[GradingAgent] LLM Response received:', response.substring(0, 200) + '...');
      
      // Extract JSON from response
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        console.warn('[GradingAgent] No JSON found in LLM response, using fallback');
        throw new Error('No JSON found in response');
      }
      
      const parsed = JSON.parse(jsonMatch);
      console.log('[GradingAgent] Parsed LLM response:', parsed);
      
      return {
        studentAnswer,
        correctAnswer,
        similarityScore: parsed.similarityScore || 'Medium',
        feedback: parsed.feedback || 'No feedback provided',
        suggestions: parsed.suggestions || '',
        strengths: parsed.strengths || '',
        weaknesses: parsed.weaknesses || '',
        value: Math.min(Math.max(parsed.value || 0, 0), maxScore),
        maxScore,
        additionalContext: '',
        gradedByLLM: true // Flag to indicate LLM was used
      };
    } catch (error) {
      console.error('[GradingAgent] Error parsing LLM response:', error);
      console.error('[GradingAgent] Raw response was:', response);
      console.warn('[GradingAgent] Falling back to text comparison');
      
      // Fallback: simple string comparison
      const fallbackResult = this.createFallbackResult(correctAnswer, studentAnswer, maxScore);
      fallbackResult.gradedByLLM = false; // Flag to indicate fallback was used
      return fallbackResult;
    }
  }

  createFallbackResult(correctAnswer, studentAnswer, maxScore) {
    const similarity = this.calculateStringSimilarity(correctAnswer, studentAnswer);
    let similarityScore = 'Low';
    if (similarity > 0.7) similarityScore = 'High';
    else if (similarity > 0.4) similarityScore = 'Medium';
    
    return {
      studentAnswer,
      correctAnswer,
      similarityScore,
      feedback: 'Automatic grading failed. Using basic similarity comparison.',
      suggestions: 'Please review your answer and ensure it addresses all parts of the question.',
      strengths: similarity > 0.3 ? 'Shows some understanding of the topic' : '',
      weaknesses: 'Answer may need more detail or accuracy',
      value: Math.round(similarity * maxScore),
      maxScore,
      additionalContext: ''
    };
  }

  createNoMatchResult(studentAnswer) {
    return {
      questionNumber: studentAnswer.questionNumber,
      studentAnswer: studentAnswer.answer,
      correctAnswer: 'No matching question found',
      similarityScore: 'N/A',
      feedback: 'No matching question found in answer key',
      suggestions: 'Please check question numbering',
      value: 0,
      maxScore: 0,
      additionalContext: ''
    };
  }

  createErrorResult(studentAnswer, matchingQuestion, errorMessage) {
    return {
      questionNumber: studentAnswer.questionNumber,
      studentAnswer: studentAnswer.answer,
      correctAnswer: matchingQuestion?.correctAnswer || 'Error occurred',
      similarityScore: 'Error',
      feedback: `Grading failed: ${errorMessage}`,
      suggestions: 'Please try again or contact support',
      value: 0,
      maxScore: matchingQuestion?.maxScore || 0,
      additionalContext: ''
    };
  }
  
  calculateStringSimilarity(str1, str2) {
    // Improved Jaccard similarity with preprocessing
    const preprocess = (str) => {
      return str.toLowerCase()
        .replace(/[^\w\s]/g, '') // Remove punctuation
        .split(/\s+/)
        .filter(word => word.length > 2); // Filter short words
    };
    
    const set1 = new Set(preprocess(str1));
    const set2 = new Set(preprocess(str2));
    
    const intersection = new Set([...set1].filter(x => set2.has(x)));
    const union = new Set([...set1, ...set2]);
    
    return union.size === 0 ? 0 : intersection.size / union.size;
  }

  async detectPlagiarismAndAI(studentAnswer, questionContext) {
    // Simple AI detection patterns
    const aiIndicators = [
      /as an ai/i,
      /i don't have personal/i,
      /based on my training/i,
      /i cannot provide/i,
      /as a language model/i,
      /i'm sorry, but i/i
    ];
    
    const hasAIIndicators = aiIndicators.some(pattern => pattern.test(studentAnswer));
    
    // Plagiarism detection via similarity to common sources
    const plagiarismCheck = await this.checkAgainstCommonSources(studentAnswer, questionContext);
    
    return {
      aiDetected: hasAIIndicators,
      aiConfidence: hasAIIndicators ? 85 : 15,
      plagiarismRisk: plagiarismCheck.risk,
      plagiarismConfidence: plagiarismCheck.confidence,
      similarSources: plagiarismCheck.sources,
      flags: this.generateIntegrityFlags(hasAIIndicators, plagiarismCheck)
    };
  }

  async checkAgainstCommonSources(answer, context) {
    // Basic pattern matching for common plagiarism indicators
    const suspiciousPatterns = [
      /according to wikipedia/i,
      /as stated in/i,
      /research shows that/i,
      /studies have shown/i,
      /it is well known that/i
    ];
    
    const riskScore = suspiciousPatterns.reduce((score, pattern) => {
      return score + (pattern.test(answer) ? 0.2 : 0);
    }, 0);
    
    // Check for unusual vocabulary complexity
    const complexWords = answer.match(/\b\w{10,}\b/g) || [];
    const vocabularyRisk = complexWords.length > (answer.split(' ').length * 0.1) ? 0.3 : 0;
    
    const totalRisk = Math.min(riskScore + vocabularyRisk, 1.0);
    
    return {
      risk: totalRisk > 0.6 ? 'high' : totalRisk > 0.3 ? 'medium' : 'low',
      sources: [], // Would integrate with actual plagiarism detection APIs
      confidence: Math.min(totalRisk * 100, 100)
    };
  }

  generateIntegrityFlags(hasAI, plagiarismCheck) {
    const flags = [];
    
    if (hasAI) {
      flags.push('Possible AI-generated content detected');
    }
    
    if (plagiarismCheck.risk === 'high') {
      flags.push('High plagiarism risk');
    } else if (plagiarismCheck.risk === 'medium') {
      flags.push('Moderate plagiarism risk');
    }
    
    return flags;
  }
  
  async fetchAdditionalContext(topic) {
    try {
      // Extract key terms for Wikipedia search
      const searchTerm = topic.split(' ').slice(0, 3).join(' ');
      
      const response = await fetch(
        `https://en.wikipedia.org/w/api.php?action=query&format=json&prop=extracts&exintro=true&explaintext=true&titles=${encodeURIComponent(searchTerm)}&origin=*`,
        {
          headers: {
            'Accept': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Wikipedia API error: ${response.status}`);
      }
      
      const data = await response.json();
      const pages = data.query?.pages;
      const pageArray = Object.values(pages || {});
      const page = pageArray[0];
      
      if (page?.extract) {
        return page.extract.substring(0, 500) + '...';
      }
      
      return 'No additional context found.';
    } catch (error) {
      console.error('Error fetching additional context:', error);
      return 'Unable to fetch additional context.';
    }
  }

  // Analytics and Learning Insights
  generateAnalytics(results) {
    return {
      overallPerformance: this.calculateOverallPerformance(results),
      questionAnalysis: this.analyzeQuestionPerformance(results),
      learningGaps: this.identifyLearningGaps(results),
      recommendations: this.generateRecommendations(results),
      integrityReport: this.generateIntegrityReport(results),
      difficultyAnalysis: this.analyzeDifficultyCorrelation(results)
    };
  }

  calculateOverallPerformance(results) {
    const totalScore = results.reduce((sum, r) => sum + (r.value || 0), 0);
    const maxPossible = results.reduce((sum, r) => sum + (r.maxScore || 0), 0);
    const percentage = maxPossible > 0 ? (totalScore / maxPossible) * 100 : 0;
    
    return {
      totalScore,
      maxPossible,
      percentage: Math.round(percentage),
      grade: this.calculateLetterGrade(percentage),
      questionsAnswered: results.length,
      averageScore: results.length > 0 ? Math.round(totalScore / results.length) : 0
    };
  }

  calculateLetterGrade(percentage) {
    if (percentage >= 97) return 'A+';
    if (percentage >= 93) return 'A';
    if (percentage >= 90) return 'A-';
    if (percentage >= 87) return 'B+';
    if (percentage >= 83) return 'B';
    if (percentage >= 80) return 'B-';
    if (percentage >= 77) return 'C+';
    if (percentage >= 73) return 'C';
    if (percentage >= 70) return 'C-';
    if (percentage >= 67) return 'D+';
    if (percentage >= 65) return 'D';
    return 'F';
  }

  analyzeQuestionPerformance(results) {
    return results.map(result => ({
      questionNumber: result.questionNumber,
      scorePercentage: Math.round((result.value / result.maxScore) * 100),
      similarityLevel: result.similarityScore,
      hasIntegrityIssues: result.integrityCheck?.flags?.length > 0,
      needsReview: result.similarityScore === 'Low' || (result.value / result.maxScore) < 0.5
    }));
  }

  identifyLearningGaps(results) {
    const lowPerformanceQuestions = results.filter(r => (r.value / r.maxScore) < 0.6);
    
    return lowPerformanceQuestions.map(result => ({
      questionNumber: result.questionNumber,
      topic: this.extractTopic(result.correctAnswer),
      scorePercentage: Math.round((result.value / result.maxScore) * 100),
      commonMistakes: this.identifyCommonMistakes(result),
      suggestedResources: this.suggestStudyResources(result),
      priority: result.maxScore > 10 ? 'high' : 'medium'
    }));
  }

  extractTopic(answer) {
    // Simple topic extraction - could be enhanced with NLP
    const words = answer.toLowerCase().split(/\s+/);
    const importantWords = words.filter(word => 
      word.length > 4 && 
      !['the', 'and', 'that', 'with', 'this', 'they', 'from', 'have', 'more', 'will', 'been'].includes(word)
    );
    return importantWords.slice(0, 3).join(', ');
  }

  identifyCommonMistakes(result) {
    const mistakes = [];
    
    if (result.similarityScore === 'Low') {
      mistakes.push('Answer does not address the question properly');
    }
    
    if (result.weaknesses) {
      mistakes.push(result.weaknesses);
    }
    
    return mistakes;
  }

  suggestStudyResources(result) {
    const topic = this.extractTopic(result.correctAnswer);
    return [
      `Review textbook sections on: ${topic}`,
      `Practice problems related to: ${topic}`,
      `Seek help understanding: ${topic}`
    ];
  }

  generateRecommendations(results) {
    const totalScore = results.reduce((sum, r) => sum + r.value, 0);
    const maxPossible = results.reduce((sum, r) => sum + r.maxScore, 0);
    const percentage = (totalScore / maxPossible) * 100;
    
    const recommendations = [];
    
    if (percentage >= 90) {
      recommendations.push("Excellent work! Consider exploring advanced topics in this subject.");
    } else if (percentage >= 80) {
      recommendations.push("Good performance. Focus on the areas where you lost points.");
    } else if (percentage >= 70) {
      recommendations.push("Solid understanding. Review concepts and practice more problems.");
    } else {
      recommendations.push("Significant study needed. Start with fundamentals.");
      recommendations.push("Consider seeking additional help or tutoring.");
      recommendations.push("Break down complex topics into smaller, manageable parts.");
    }
    
    // Add specific recommendations based on patterns
    const lowScoreQuestions = results.filter(r => (r.value / r.maxScore) < 0.5);
    if (lowScoreQuestions.length > results.length * 0.5) {
      recommendations.push("Consider reviewing the entire unit - multiple concepts need reinforcement.");
    }
    
    return recommendations;
  }

  generateIntegrityReport(results) {
    const integrityIssues = results.filter(r => 
      r.integrityCheck?.flags?.length > 0
    );
    
    const aiDetected = results.filter(r => r.integrityCheck?.aiDetected).length;
    const plagiarismRisk = results.filter(r => 
      r.integrityCheck?.plagiarismRisk === 'high'
    ).length;
    
    return {
      totalQuestions: results.length,
      questionsWithIssues: integrityIssues.length,
      aiDetectedCount: aiDetected,
      highPlagiarismRisk: plagiarismRisk,
      overallRisk: integrityIssues.length > results.length * 0.3 ? 'high' : 
                   integrityIssues.length > 0 ? 'medium' : 'low',
      recommendations: this.generateIntegrityRecommendations(integrityIssues.length, results.length)
    };
  }

  generateIntegrityRecommendations(issueCount, totalCount) {
    if (issueCount === 0) {
      return ["No integrity issues detected. Good academic honesty practices."];
    }
    
    const issuePercentage = (issueCount / totalCount) * 100;
    const recommendations = [];
    
    if (issuePercentage > 50) {
      recommendations.push("High number of integrity flags. Manual review recommended.");
      recommendations.push("Consider discussing academic honesty policies with student.");
    } else if (issuePercentage > 20) {
      recommendations.push("Some integrity concerns detected. Spot check recommended.");
    } else {
      recommendations.push("Minor integrity flags detected. Monitor for patterns.");
    }
    
    return recommendations;
  }

  analyzeDifficultyCorrelation(results) {
    // Simple difficulty analysis based on performance
    const questionPerformance = results.map(r => ({
      questionNumber: r.questionNumber,
      scorePercentage: (r.value / r.maxScore) * 100,
      maxScore: r.maxScore
    }));
    
    const avgPerformance = questionPerformance.reduce((sum, q) => sum + q.scorePercentage, 0) / questionPerformance.length;
    
    return {
      averagePerformance: Math.round(avgPerformance),
      easiestQuestions: questionPerformance
        .filter(q => q.scorePercentage > avgPerformance + 20)
        .map(q => q.questionNumber),
      hardestQuestions: questionPerformance
        .filter(q => q.scorePercentage < avgPerformance - 20)
        .map(q => q.questionNumber),
      performanceDistribution: {
        excellent: questionPerformance.filter(q => q.scorePercentage >= 90).length,
        good: questionPerformance.filter(q => q.scorePercentage >= 70 && q.scorePercentage < 90).length,
        fair: questionPerformance.filter(q => q.scorePercentage >= 50 && q.scorePercentage < 70).length,
        poor: questionPerformance.filter(q => q.scorePercentage < 50).length
      }
    };
  }
}

export default GradingAgent;